// Mensajes de SignalR del modulo de Eventos.
//
// Viajan por la MISMA conexion que ya mantiene `sharedChatHubConnection`
// (`${REACT_APP_BASE_URL_CHAT}/chatHub`), igual que los de publicaciones y foro:
// que el endpoint HTTP viva en otra base (`REACT_APP_BASE_URL_PUBLISH`) no
// significa que el hub sea otro. Por eso aqui no se abre ninguna conexion nueva;
// este archivo solo nombra los mensajes y normaliza su contenido.
//
// Tabla del contrato — endpoint que lo provoca -> mensaje que llega:
//
//   POST /api/generar/eventos                 -> ReceiveNewEvent
//   POST /api/generar/evento-modalidad        -> ReceiveNewEvent
//   POST /api/editar/eventos                  -> ReceiveEditEvent
//   POST /api/editar/evento-modalidad         -> ReceiveEditEvent
//   POST /api/cancelar/mievento               -> ReceiveCancelEvent
//   POST /api/cancelar/evento-modalidad       -> ReceiveCancelEvent
//   POST /api/eliminar/evento                 -> ReceiveDeleteEvent
//   POST /api/eliminar/evento-modalidad       -> ReceiveDeleteEvent
//   POST /api/compartir/evento                -> ReceiveShareEvent
//   POST /api/comprar/boleto-evento           -> ReceiveBuyEventTicket
//   POST /api/registrar/asistencia-evento     -> ReceiveEventAttendance
//   POST /api/cancelar/registro-asistente     -> ReceiveEventAttendance
//   POST /api/comentar/evento                 -> ReceiveCommentEvent
//   POST /api/actualizar-comentario/evento    -> ReceiveEditCommentEvent
//   POST /api/eliminar-comentario/evento      -> ReceiveDeleteCommentEvent
//   POST /api/reaccionar/evento               -> ReceiveReactionEvent
//   POST /api/reaccionar-comentario/evento    -> ReceiveReactionCommentEvent
//   POST /api/reportar/evento                 -> ReceiveReportEvent
//   POST /api/insertar/fotos-evento           -> ReceiveEventPhoto

// Evento de navegador con el que la aplicacion reparte lo que llego por el hub.
// Mismo mecanismo que `feed:realtime-event`: una sola conexion en el proveedor y
// las pantallas se suscriben a la ventana, sin abrir sockets por pantalla.
export const EVENT_REALTIME_BROWSER_EVENT = "events:realtime-event";

// Ciclo de vida del evento: lo que cambia la tarjeta y la cabecera del detalle.
export const EVENT_LIFECYCLE_REALTIME_EVENTS = [
  "ReceiveNewEvent",
  "ReceiveEditEvent",
  "ReceiveCancelEvent",
  "ReceiveDeleteEvent",
  "ReceiveShareEvent",
  "ReceiveReportEvent",
];

// Asistencia: confirmar, cancelar el registro y comprar boleto. Son los que
// cambian el estado que pinta la tarjeta (`confirmoAsistencia`, `esInteresado`)
// y la lista de invitados del detalle.
export const EVENT_ATTENDANCE_REALTIME_EVENTS = [
  "ReceiveEventAttendance",
  "ReceiveBuyEventTicket",
];

// Hilo de comentarios del evento, incluida la reaccion a un comentario.
export const EVENT_COMMENT_REALTIME_EVENTS = [
  "ReceiveCommentEvent",
  "ReceiveEditCommentEvent",
  "ReceiveDeleteCommentEvent",
  "ReceiveReactionCommentEvent",
];

// Reaccion al evento y galeria de fotos: solo afectan al detalle.
export const EVENT_CONTENT_REALTIME_EVENTS = [
  "ReceiveReactionEvent",
  "ReceiveEventPhoto",
];

// Lista unica para registrar/soltar los manejadores en el hub.
export const EVENT_REALTIME_EVENTS = [
  ...EVENT_LIFECYCLE_REALTIME_EVENTS,
  ...EVENT_ATTENDANCE_REALTIME_EVENTS,
  ...EVENT_COMMENT_REALTIME_EVENTS,
  ...EVENT_CONTENT_REALTIME_EVENTS,
];

// Lo que refresca el LISTADO de eventos. Deja fuera comentarios, reacciones y
// fotos a proposito: la tarjeta no muestra ninguno de esos datos, asi que pedir
// el feed completo por un comentario seria trabajo que no cambia nada en pantalla.
export const EVENT_LIST_REALTIME_EVENTS = new Set([
  ...EVENT_LIFECYCLE_REALTIME_EVENTS,
  ...EVENT_ATTENDANCE_REALTIME_EVENTS,
]);

// Lo que refresca el DETALLE de un evento (sin contar el hilo de comentarios,
// que se recarga aparte porque tiene su propio endpoint).
export const EVENT_DETAIL_REALTIME_EVENTS = new Set([
  ...EVENT_LIFECYCLE_REALTIME_EVENTS,
  ...EVENT_CONTENT_REALTIME_EVENTS,
]);

export const EVENT_ATTENDANCE_REALTIME_EVENTS_SET = new Set(
  EVENT_ATTENDANCE_REALTIME_EVENTS
);

export const EVENT_COMMENT_REALTIME_EVENTS_SET = new Set(
  EVENT_COMMENT_REALTIME_EVENTS
);

export const isEventRealtimeEvent = (eventName) =>
  EVENT_REALTIME_EVENTS.includes(eventName);

// Llaves con las que el backend nombra al evento dentro del contenido. Se buscan
// SOLO estas y nunca un `id` pelado: en `ReceiveCommentEvent` el `id` generico es
// el del comentario, y confundirlos haria que el detalle abierto ignore un cambio
// que si le toca (o al reves, que recargue por un evento ajeno).
const EVENT_ID_KEY = /^(id)?evento(id)?$/i;
const EVENT_ID_KEY_EN = /^event(id)?$/i;

// Contenedores donde el backend suele anidar el objeto del evento.
const NESTED_KEYS = ["data", "payload", "evento", "event", "detalle", "result"];

const isScalar = (value) =>
  typeof value === "string" || typeof value === "number";

// Identificadores de evento que trae el mensaje. Devuelve un arreglo porque un
// mensaje puede nombrar al evento de mas de una forma (posicional y dentro del
// objeto) y no hay garantia de cual viene.
export const getEventIdsFromRealtimeArgs = (args = []) => {
  const ids = new Set();

  const visit = (value, depth = 0) => {
    if (value === null || value === undefined || depth > 3) return;

    if (Array.isArray(value)) {
      value.forEach((item) => visit(item, depth + 1));
      return;
    }

    if (typeof value !== "object") return;

    Object.entries(value).forEach(([key, item]) => {
      if (EVENT_ID_KEY.test(key) || EVENT_ID_KEY_EN.test(key)) {
        if (isScalar(item)) {
          ids.add(String(item));
          return;
        }
        // `evento: { idEvento: 77 }` cae aqui tambien: la llave nombra al evento
        // pero el valor es el objeto entero, asi que hay que entrar en vez de
        // descartarlo —si no, un mensaje anidado se queda sin id y recargaria
        // cualquier pantalla abierta.
        visit(item, depth + 1);
        return;
      }
      if (NESTED_KEYS.includes(key)) visit(item, depth + 1);
    });
  };

  const lista = Array.isArray(args) ? args : [args];

  // El primer argumento suelto se toma como el id del evento: es la forma en que
  // el hub manda los avisos de una sola pieza (`ReceiveCancelEvent(idEvento)`).
  // Del segundo en adelante no se asume nada, porque ahi caben el id del
  // comentario o el del usuario.
  if (isScalar(lista[0])) ids.add(String(lista[0]));

  lista.forEach((item) => visit(item));

  return Array.from(ids).filter(Boolean);
};

// Un mensaje le toca a esta pantalla si nombra a su evento, o si no nombra a
// ninguno. El segundo caso es deliberado: ante un contenido que no reconocemos
// se prefiere refrescar de mas —cuesta una peticion— a dejar la pantalla con
// datos viejos, que es el defecto que este trabajo viene a quitar.
export const realtimeEventMatchesEvent = (args, eventId) => {
  if (!eventId) return false;

  const ids = getEventIdsFromRealtimeArgs(args);
  if (ids.length === 0) return true;

  return ids.some((id) => String(id) === String(eventId));
};

// Suscripcion al evento de navegador. Centralizada aqui para que las pantallas no
// repitan el nombre de la cadena ni la forma del `detail`.
export const subscribeToEventRealtime = (listener) => {
  const handler = (browserEvent) => {
    const eventName = browserEvent?.detail?.eventName;
    if (!isEventRealtimeEvent(eventName)) return;

    listener({ eventName, args: browserEvent?.detail?.args || [] });
  };

  window.addEventListener(EVENT_REALTIME_BROWSER_EVENT, handler);

  return () => {
    window.removeEventListener(EVENT_REALTIME_BROWSER_EVENT, handler);
  };
};
